// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import {
  Show,
  createEffect,
  createSignal,
  onCleanup,
  onMount,
  type ParentProps,
} from "solid-js";
import { useQueryClient } from "@tanstack/solid-query";
import { listAccounts } from "../api/generated";
import { authSlotHeaders } from "../api/runtime";
import { Button } from "../components/forms";
import { startLogin } from ".";
import { expiredSession, restoreSession } from "./state";

export function SessionBoundary(p: ParentProps) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");
  let application!: HTMLDivElement;
  let signIn!: HTMLButtonElement;

  const retry = async (showError: boolean) => {
    const slot = expiredSession()?.slot;
    if (slot === undefined || busy()) return;
    setBusy(true);
    setError("");
    try {
      const result = await listAccounts({
        throwOnError: false,
        responseStyle: "fields",
        headers: authSlotHeaders(slot),
      });
      if (result.response?.ok) {
        restoreSession(slot);
        await queryClient.refetchQueries({
          type: "active",
          predicate: (query) => query.queryKey[0] === slot,
        });
      } else if (showError) {
        setError("You’re still signed out. Sign in again to continue.");
      }
    } catch {
      if (showError) setError("Unable to try again. Check your connection.");
    }
    setBusy(false);
  };

  createEffect(() => {
    const expired = expiredSession() !== undefined;
    application.inert = expired;
    if (expired) queueMicrotask(() => signIn?.focus());
  });
  onMount(() => {
    const retryOnFocus = () => {
      if (document.visibilityState === "visible") void retry(false);
    };
    window.addEventListener("focus", retryOnFocus);
    document.addEventListener("visibilitychange", retryOnFocus);
    onCleanup(() => {
      window.removeEventListener("focus", retryOnFocus);
      document.removeEventListener("visibilitychange", retryOnFocus);
    });
  });

  return (
    <>
      <div ref={application} aria-hidden={expiredSession() !== undefined}>
        {p.children}
      </div>
      <Show when={expiredSession()} keyed>
        {(session) => (
          <div class="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 p-4">
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="session-expired-title"
              aria-describedby="session-expired-copy"
              class="panel w-full max-w-md p-6 sm:p-8"
            >
              <h1 id="session-expired-title" class="text-2xl font-semibold">
                Your session has expired
              </h1>
              <p id="session-expired-copy" class="mt-2 text-slate-600">
                Sign in again to continue. If you signed in from another tab,
                try again.
              </p>
              <Show when={error()}>
                <p role="alert" class="mt-4 text-sm text-error">
                  {error()}
                </p>
              </Show>
              <div class="mt-6 flex flex-wrap gap-3">
                <button
                  ref={signIn}
                  type="button"
                  class="btn btn-primary"
                  disabled={busy()}
                  onClick={async () => {
                    setBusy(true);
                    setError("");
                    try {
                      await startLogin(
                        session.slot,
                        `${location.pathname}${location.search}${location.hash}`,
                      );
                    } catch {
                      setError("Sign in could not be started. Please try again.");
                      setBusy(false);
                    }
                  }}
                >
                  Sign in
                </button>
                <Button
                  kind="btn-outline"
                  disabled={busy()}
                  onClick={() => void retry(true)}
                >
                  {busy() ? "Trying…" : "Try again"}
                </Button>
              </div>
            </section>
          </div>
        )}
      </Show>
    </>
  );
}
